import { useEffect, useRef, useMemo } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, PointerLockControls, Grid, Environment, TransformControls } from "@react-three/drei";
import * as THREE from "three";

export const NEW_PHASES = ["framing", "drywall", "finish"];
export const REMODEL_PHASES = ["demolition", "rough-in", "finish"];
export const PHASE_LABEL = { framing: "Framing", drywall: "Drywall", finish: "Finish", demolition: "Demolition", "rough-in": "Rough-in" };

// Floor finish materials — visual surfaces that update from catalog/finish selections.
export const FLOOR_MAT = {
  hardwood: { color: "#a9744f", roughness: 0.65, metalness: 0 },
  wood: { color: "#b08968", roughness: 0.7, metalness: 0 },
  laminate: { color: "#c9a87c", roughness: 0.55, metalness: 0 },
  tile: { color: "#d4d4d8", roughness: 0.3, metalness: 0.05 },
  marble: { color: "#eceae7", roughness: 0.12, metalness: 0.15 },
  carpet: { color: "#9ca3af", roughness: 1, metalness: 0 },
  concrete: { color: "#a8a29e", roughness: 0.9, metalness: 0 },
  vinyl: { color: "#cbb494", roughness: 0.5, metalness: 0 },
};
export const FLOOR_OPTIONS = Object.keys(FLOOR_MAT);

function wallMode(kind, phase) {
  if (kind === "remodel") return phase === "demolition" ? "ghost" : phase === "rough-in" ? "studs" : "color";
  return phase === "framing" ? "studs" : phase === "drywall" ? "gray" : "color";
}

function solidRanges(length, gaps) {
  let ranges = [[0, length]];
  gaps.forEach(([gs, ge]) => {
    const next = [];
    ranges.forEach(([s, e]) => {
      if (ge <= s || gs >= e) { next.push([s, e]); return; }
      if (gs > s) next.push([s, Math.min(gs, e)]);
      if (ge < e) next.push([Math.max(ge, s), e]);
    });
    ranges = next;
  });
  return ranges.filter(([s, e]) => e - s > 0.05);
}

function Segment({ horizontal, start, at, s, e, height, thickness, mode, color }) {
  const len = e - s;
  const mid = s + len / 2;
  const px = horizontal ? start.x + mid : at;
  const pz = horizontal ? at : start.z + mid;
  const size = horizontal ? [len, height, thickness] : [thickness, height, len];
  if (mode === "studs") {
    const studs = [];
    const n = Math.max(2, Math.floor(len / 1.33));
    for (let i = 0; i <= n; i++) {
      const t = s + (len * i) / n;
      studs.push(horizontal ? [start.x + t, height / 2, at] : [at, height / 2, start.z + t]);
    }
    const plate = horizontal ? [len, 0.25, thickness] : [thickness, 0.25, len];
    return (
      <group>
        {studs.map((p, i) => (
          <mesh key={i} position={p} castShadow>
            <boxGeometry args={horizontal ? [0.25, height, thickness] : [thickness, height, 0.25]} />
            <meshStandardMaterial color="#c8945f" />
          </mesh>
        ))}
        <mesh position={[px, 0.12, pz]}><boxGeometry args={plate} /><meshStandardMaterial color="#8a5a2b" /></mesh>
        <mesh position={[px, height - 0.12, pz]}><boxGeometry args={plate} /><meshStandardMaterial color="#8a5a2b" /></mesh>
      </group>
    );
  }
  return (
    <mesh position={[px, height / 2, pz]} castShadow receiveShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial color={mode === "gray" ? "#d1d5db" : color} transparent={mode === "ghost"} opacity={mode === "ghost" ? 0.18 : 1} roughness={0.85} />
    </mesh>
  );
}

function Wall({ ax, az, bx, bz, height, thickness, openings, mode, color }) {
  const horizontal = az === bz;
  const length = horizontal ? Math.abs(bx - ax) : Math.abs(bz - az);
  const start = { x: Math.min(ax, bx), z: Math.min(az, bz) };
  const at = horizontal ? az : ax;
  const gaps = [], headers = [], windows = [];
  (openings || []).forEach((o) => {
    const c = o.offset * length;
    const w = Math.min(o.width, length - 0.2);
    const g = [Math.max(0, c - w / 2), Math.min(length, c + w / 2)];
    if (o.type === "door" || o.type === "arch") {
      gaps.push(g);
      const oh = o.type === "arch" ? Math.min(height - 0.3, (o.height || 8) + 1) : (o.height || 7);
      if (oh < height) headers.push({ g, oh });
    } else if (o.type === "window" && (mode === "color" || mode === "gray")) {
      windows.push({ g, wh: o.height || 4 });
    }
  });
  const ranges = solidRanges(length, gaps);
  return (
    <group>
      {ranges.map(([s, e], i) => (
        <Segment key={i} horizontal={horizontal} start={start} at={at} s={s} e={e} height={height} thickness={thickness} mode={mode} color={color} />
      ))}
      {mode !== "studs" && mode !== "ghost" && headers.map((h, i) => {
        const len = h.g[1] - h.g[0]; const mid = h.g[0] + len / 2;
        const px = horizontal ? start.x + mid : at;
        const pz = horizontal ? at : start.z + mid;
        const hh = height - h.oh;
        return (
          <mesh key={`h${i}`} position={[px, h.oh + hh / 2, pz]}>
            <boxGeometry args={horizontal ? [len, hh, thickness] : [thickness, hh, len]} />
            <meshStandardMaterial color={mode === "gray" ? "#d1d5db" : color} />
          </mesh>
        );
      })}
      {windows.map((wn, i) => {
        const len = wn.g[1] - wn.g[0]; const mid = wn.g[0] + len / 2;
        const px = horizontal ? start.x + mid : at;
        const pz = horizontal ? at : start.z + mid;
        return (
          <mesh key={`w${i}`} position={[px, height * 0.5, pz]}>
            <boxGeometry args={horizontal ? [len, wn.wh, thickness + 0.1] : [thickness + 0.1, wn.wh, len]} />
            <meshStandardMaterial color="#7dd3fc" transparent opacity={0.45} metalness={0.2} roughness={0.1} />
          </mesh>
        );
      })}
    </group>
  );
}

function Room({ room, openings, kind, phase, selected, onSelect }) {
  const mode = wallMode(kind, phase);
  const { x, z, w, d, height } = room;
  const th = 0.4;
  const wo = (wall) => openings.filter((o) => o.room_id === room.id && o.wall === wall);
  const mat = FLOOR_MAT[room.floor] || FLOOR_MAT.wood;
  const showFloor = mode !== "studs" || phase !== "demolition";
  return (
    <group onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(room.id); } : undefined}>
      {showFloor && (
        <mesh position={[x + w / 2, 0.05, z + d / 2]} receiveShadow>
          <boxGeometry args={[w, 0.1, d]} />
          <meshStandardMaterial color={selected ? "#f59e0b" : mat.color} roughness={mat.roughness} metalness={mat.metalness} />
        </mesh>
      )}
      <Wall ax={x} az={z} bx={x + w} bz={z} height={height} thickness={th} color={room.wall_color} mode={mode} openings={wo("n")} />
      <Wall ax={x} az={z + d} bx={x + w} bz={z + d} height={height} thickness={th} color={room.wall_color} mode={mode} openings={wo("s")} />
      <Wall ax={x} az={z} bx={x} bz={z + d} height={height} thickness={th} color={room.wall_color} mode={mode} openings={wo("w")} />
      <Wall ax={x + w} az={z} bx={x + w} bz={z + d} height={height} thickness={th} color={room.wall_color} mode={mode} openings={wo("e")} />
    </group>
  );
}

function Exterior({ e }) {
  const raised = e.type !== "patio";
  const y = raised ? 0.4 : 0.06;
  const col = e.type === "deck" ? "#a16207" : e.type === "porch" ? "#9a6a3a" : "#9ca3af";
  const posts = raised ? [[e.x + 0.5, e.z + 0.5], [e.x + e.w - 0.5, e.z + 0.5], [e.x + 0.5, e.z + e.d - 0.5], [e.x + e.w - 0.5, e.z + e.d - 0.5]] : [];
  return (
    <group>
      <mesh position={[e.x + e.w / 2, y, e.z + e.d / 2]} receiveShadow>
        <boxGeometry args={[e.w, raised ? 0.3 : 0.12, e.d]} />
        <meshStandardMaterial color={col} roughness={0.9} />
      </mesh>
      {posts.map((p, i) => (
        <mesh key={i} position={[p[0], 4, p[1]]}><boxGeometry args={[0.35, 8, 0.35]} /><meshStandardMaterial color="#8a5a2b" /></mesh>
      ))}
    </group>
  );
}

function WalkRig() {
  const { camera } = useThree();
  const keys = useRef({});
  useEffect(() => {
    const d = (e) => (keys.current[e.code] = true);
    const u = (e) => (keys.current[e.code] = false);
    window.addEventListener("keydown", d);
    window.addEventListener("keyup", u);
    return () => { window.removeEventListener("keydown", d); window.removeEventListener("keyup", u); };
  }, []);
  useFrame((_, dt) => {
    const sp = 16 * dt;
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir); dir.y = 0; dir.normalize();
    const right = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(0, 1, 0));
    if (keys.current.KeyW) camera.position.addScaledVector(dir, sp);
    if (keys.current.KeyS) camera.position.addScaledVector(dir, -sp);
    if (keys.current.KeyD) camera.position.addScaledVector(right, sp);
    if (keys.current.KeyA) camera.position.addScaledVector(right, -sp);
    camera.position.y = 5;
  });
  return null;
}

function box(pos, size, color, key) {
  return <mesh key={key} position={pos} castShadow receiveShadow><boxGeometry args={size} /><meshStandardMaterial color={color} roughness={0.75} /></mesh>;
}

function Furniture({ room }) {
  const t = `${room.type || ""} ${room.name || ""}`.toLowerCase();
  const cx = room.x + room.w / 2, cz = room.z + room.d / 2;
  const items = [];
  const add = (p, s, c) => items.push(box(p, s, c, `${room.id}-${items.length}`));
  if (/bed/.test(t)) {
    add([cx, 1, cz], [6.5, 1, 7], "#6b7280"); add([cx, 1.7, cz - 3.2], [6.5, 1.4, 0.6], "#374151");
    add([cx - 3.6, 0.9, cz - 2.8], [1.2, 0.9, 1.2], "#4b5563"); add([cx + 3.6, 0.9, cz - 2.8], [1.2, 0.9, 1.2], "#4b5563");
  } else if (/kitchen/.test(t)) {
    add([room.x + room.w / 2, 1.5, room.z + 1.2], [room.w - 2, 3, 2], "#9ca3af");
    add([cx, 1.6, cz], [5, 3.2, 3], "#7c6f64"); add([room.x + 1.5, 3, room.z + room.d - 1.4], [2.5, 6, 2.2], "#d1d5db");
  } else if (/bath/.test(t)) {
    add([room.x + room.w / 2, 1, room.z + 1.4], [Math.max(3, room.w - 3), 2, 3], "#e5e7eb");
    add([room.x + room.w - 1.6, 1.6, room.z + room.d - 1.6], [2.4, 3, 2], "#cbd5e1");
    add([room.x + 1.3, 1.1, room.z + room.d - 1.3], [1.6, 2.2, 1.6], "#f3f4f6");
  } else if (/living|family|great/.test(t)) {
    add([cx - 2, 1.2, cz + room.d / 2 - 1.8], [7, 2.4, 3], "#4b5563");
    add([cx - 2, 0.8, cz], [4, 1.2, 2.2], "#6b4f3a"); add([cx - 2, 2, room.z + 0.9], [6, 3.5, 0.6], "#1f2937");
  } else if (/dining/.test(t)) {
    add([cx, 1.4, cz], [6, 0.4, 3.5], "#6b4f3a");
    [[-2.2, -1.6], [0, -1.6], [2.2, -1.6], [-2.2, 1.6], [0, 1.6], [2.2, 1.6]].forEach(([dx, dz]) => add([cx + dx, 1, cz + dz], [1, 2, 1], "#4b5563"));
  } else if (/office|study/.test(t)) {
    add([cx, 1.4, room.z + 1.6], [5, 0.3, 2.5], "#6b4f3a"); add([cx, 1, cz], [1.8, 2, 1.8], "#374151");
  } else {
    add([cx, 1, cz], [3, 1.8, 3], "#6b7280");
  }
  return <group>{items}</group>;
}

export const ITEM_PRESETS = {
  sofa: { label: "Sofa", size: [7, 2.4, 3], color: "#4b5563", cat: "Living" },
  armchair: { label: "Armchair", size: [3, 2.4, 3], color: "#6b7280", cat: "Living" },
  coffee_table: { label: "Coffee Table", size: [4, 1.2, 2.2], color: "#6b4f3a", cat: "Living" },
  tv_unit: { label: "TV Unit", size: [6, 1.6, 1.4], color: "#1f2937", cat: "Living" },
  bookshelf: { label: "Bookshelf", size: [3, 7, 1.2], color: "#5b5048", cat: "Living" },
  bed: { label: "Bed", size: [6.5, 1.6, 7], color: "#6b7280", cat: "Bedroom" },
  nightstand: { label: "Nightstand", size: [1.4, 1.6, 1.4], color: "#4b5563", cat: "Bedroom" },
  dresser: { label: "Dresser", size: [4, 2.6, 1.6], color: "#7c6f64", cat: "Bedroom" },
  wardrobe: { label: "Wardrobe", size: [4, 7, 2], color: "#5b5048", cat: "Bedroom" },
  dining_table: { label: "Dining Table", size: [6, 1.4, 3.5], color: "#6b4f3a", cat: "Dining" },
  chair: { label: "Chair", size: [1.5, 3, 1.5], color: "#4b5563", cat: "Dining" },
  desk: { label: "Desk", size: [5, 2.4, 2.5], color: "#6b4f3a", cat: "Office" },
  fridge: { label: "Refrigerator", size: [3, 6, 2.8], color: "#d1d5db", cat: "Appliances" },
  stove: { label: "Stove / Range", size: [2.5, 3, 2.5], color: "#9ca3af", cat: "Appliances" },
  counter: { label: "Counter", size: [8, 3, 2], color: "#9ca3af", cat: "Appliances" },
  island: { label: "Kitchen Island", size: [5, 3.2, 3], color: "#7c6f64", cat: "Appliances" },
  washer: { label: "Washer / Dryer", size: [2.5, 3.4, 2.5], color: "#e5e7eb", cat: "Appliances" },
  toilet: { label: "Toilet", size: [1.6, 2.2, 1.6], color: "#f3f4f6", cat: "Bath" },
  vanity: { label: "Vanity / Sink", size: [3, 2, 2], color: "#e5e7eb", cat: "Bath" },
  bathtub: { label: "Bathtub", size: [5, 2, 3], color: "#e5e7eb", cat: "Bath" },
  shower: { label: "Shower", size: [3, 7, 3], color: "#cbd5e1", cat: "Bath" },
  rug: { label: "Rug", size: [6, 0.1, 4], color: "#94a3b8", cat: "Decor" },
  plant: { label: "Plant", size: [1.5, 3, 1.5], color: "#4d7c0f", cat: "Decor" },
};

function Item({ item, selected, onSelect, editable, onMove }) {
  const ref = useRef();
  const w = item.w, h = item.h, d = item.d;
  const node = (
    <group ref={ref} position={[item.x, 0, item.z]} rotation={[0, THREE.MathUtils.degToRad(item.rot || 0), 0]}
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(item.id); } : undefined}>
      <mesh position={[0, h / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={item.color || "#6b7280"} roughness={0.7} emissive={selected ? "#f59e0b" : "#000000"} emissiveIntensity={selected ? 0.35 : 0} />
      </mesh>
    </group>
  );
  if (editable && selected && onMove) {
    return (
      <TransformControls mode="translate" showY={false} translationSnap={0.5}
        onObjectChange={() => { if (ref.current) onMove(item.id, +ref.current.position.x.toFixed(2), +ref.current.position.z.toFixed(2)); }}>
        {node}
      </TransformControls>
    );
  }
  return node;
}

function CaptureBridge({ captureRef }) {
  const { gl, scene, camera, controls } = useThree();
  useEffect(() => {
    if (!captureRef) return undefined;
    captureRef.current = () => {
      gl.render(scene, camera);
      return {
        image: gl.domElement.toDataURL("image/jpeg", 0.7),
        position: camera.position.toArray(),
        target: controls?.target ? controls.target.toArray() : [0, 0, 0],
      };
    };
    return () => { if (captureRef) captureRef.current = null; };
  });
  return null;
}

function GotoView({ view }) {
  const { camera, controls } = useThree();
  useEffect(() => {
    if (view?.position) {
      camera.position.set(view.position[0], view.position[1], view.position[2]);
      if (controls?.target && view.target) { controls.target.set(view.target[0], view.target[1], view.target[2]); controls.update(); }
    }
  }, [view]); // eslint-disable-line
  return null;
}

export function FloorplanViewer({ floorplan, phase = "finish", walk = false, selected = null, onSelect = null, furnished = false, captureRef = null, gotoView = null, selItem = null, onSelectItem = null, onItemMove = null, editItems = false }) {
  const rooms = floorplan?.rooms || [];
  const center = useMemo(() => {
    if (!rooms.length) return [15, 15];
    const xs = rooms.map((r) => r.x + r.w / 2), zs = rooms.map((r) => r.z + r.d / 2);
    return [xs.reduce((a, b) => a + b, 0) / xs.length, zs.reduce((a, b) => a + b, 0) / zs.length];
  }, [rooms]);
  const showFurniture = furnished && wallMode(floorplan?.project_kind, phase) === "color";
  return (
    <Canvas shadows gl={{ preserveDrawingBuffer: true }} camera={{ position: [center[0] + 28, 30, center[1] + 32], fov: 50 }} onPointerMissed={() => { onSelect && onSelect(null); onSelectItem && onSelectItem(null); }}>
      <hemisphereLight intensity={0.6} groundColor="#334155" />
      <directionalLight position={[40, 60, 20]} intensity={1.1} castShadow shadow-mapSize={[1024, 1024]} />
      <Environment preset="city" />
      <Grid args={[200, 200]} cellColor="#1e293b" sectionColor="#334155" infiniteGrid position={[0, 0, 0]} fadeDistance={120} />
      {rooms.map((r) => (
        <Room key={r.id} room={r} openings={floorplan.openings || []} kind={floorplan.project_kind} phase={phase} selected={selected === r.id} onSelect={onSelect} />
      ))}
      {showFurniture && rooms.map((r) => <Furniture key={`f-${r.id}`} room={r} />)}
      {(floorplan?.items || []).map((it) => (
        <Item key={it.id} item={it} selected={selItem === it.id} onSelect={onSelectItem} editable={editItems && !walk} onMove={onItemMove} />
      ))}
      {(floorplan?.exterior || []).map((e) => <Exterior key={e.id} e={e} />)}
      {captureRef && <CaptureBridge captureRef={captureRef} />}
      {!walk && gotoView && <GotoView view={gotoView} />}
      {walk ? (<><PointerLockControls /><WalkRig /></>) : (<OrbitControls target={[center[0], 2, center[1]]} makeDefault />)}
    </Canvas>
  );
}
